import useLocalStorage from '../hooks/useLocalStorage';
import useQueryParams from '../hooks/useQueryParams';
import { toggleDocumentAttribute } from '../utils/layout';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
const ThemeContext = createContext(undefined);
const useLayoutContext = () => {
  const context = use(ThemeContext);
  if (!context) {
    throw new Error('useLayoutContext can only be used within LayoutProvider');
  }
  return context;
};

// const getPreferredTheme = (): ThemeType => (window.matchMedia('(przefers-color-scheme: dark)').matches ? 'dark' : 'light')

const LayoutProvider = ({
  children
}) => {
  const queryParams = useQueryParams();
  const override = !!(queryParams.layout_theme || queryParams.topbar_theme || queryParams.menu_theme || queryParams.menu_size || queryParams.layout_mode);
  const INIT_STATE = useMemo(() => ({
    theme: queryParams['layout_theme'] ? queryParams['layout_theme'] : 'light',
    layoutOrientation: queryParams['layout_type'] ? queryParams['layout_type'] : 'vertical',
    topbarTheme: queryParams['topbar_theme'] ? queryParams['topbar_theme'] : 'brand',
    menu: {
      theme: queryParams['menu_theme'] ? queryParams['menu_theme'] : 'light',
      size: queryParams['menu_size'] ? queryParams['menu_size'] : 'default'
    },
    layoutMode: queryParams['layout_mode'] ? queryParams['layout_mode'] : 'fluid'
  }), [queryParams]);
  const [settings, setSettings] = useLocalStorage('__ABSTACK_NEXT_CONFIG__', INIT_STATE, override);
  const [offcanvasStates, setOffcanvasStates] = useState({
    showThemeCustomizer: false,
    showHorizontalMenu: false,
    // showActivityStream: false,
    showBackdrop: false
  });

  // update settings
  const updateSettings = useCallback(_newSettings => setSettings({
    ...settings,
    ..._newSettings
  }), [settings, setSettings]);

  // update theme mode
  const changeTheme = useCallback(newTheme => {
    updateSettings({
      theme: newTheme
    });
  }, [updateSettings]);

  // change topbar theme
  const changeTopbarTheme = useCallback(newTheme => {
    updateSettings({
      topbarTheme: newTheme
    });
  }, [updateSettings]);
  const changeLayoutMode = useCallback(newMode => {
    updateSettings({
      layoutMode: newMode
    });
  }, [updateSettings]);
  const changeLayoutOrientation = useCallback(newOrientation => {
    updateSettings({
      layoutOrientation: newOrientation
    });
  }, [updateSettings]);

  // change menu theme
  const changeMenuTheme = useCallback(newTheme => {
    updateSettings({
      menu: {
        ...settings.menu,
        theme: newTheme
      }
    });
  }, [updateSettings, settings.menu]);

  // change menu theme
  const changeMenuSize = useCallback(newSize => {
    updateSettings({
      menu: {
        ...settings.menu,
        size: newSize
      }
    });
  }, [updateSettings, settings.menu]);

  // toggle theme customizer offcanvas
  const toggleThemeCustomizer = useCallback(() => {
    setOffcanvasStates({
      ...offcanvasStates,
      showThemeCustomizer: !offcanvasStates.showThemeCustomizer
    });
  }, [offcanvasStates]);
  const toggleHorizontalMenu = useCallback(() => {
    setOffcanvasStates({
      ...offcanvasStates,
      showHorizontalMenu: !offcanvasStates.showHorizontalMenu
    });
  }, [offcanvasStates]);
  const themeCustomizer = useMemo(() => ({
    open: offcanvasStates.showThemeCustomizer,
    toggle: toggleThemeCustomizer
  }), [offcanvasStates.showThemeCustomizer, toggleThemeCustomizer]);
  const horizontalMenu = useMemo(() => ({
    open: offcanvasStates.showHorizontalMenu,
    toggle: toggleHorizontalMenu
  }), [offcanvasStates.showHorizontalMenu, toggleHorizontalMenu]);

  // toggle backdrop
  const toggleBackdrop = useCallback(() => {
    const htmlTag = document.getElementsByTagName('html')[0];
    if (offcanvasStates.showBackdrop) htmlTag.classList.remove('sidebar-enable'); else htmlTag.classList.add('sidebar-enable');
    setOffcanvasStates({
      ...offcanvasStates,
      showBackdrop: !offcanvasStates.showBackdrop
    });
  }, [offcanvasStates]);
  useEffect(() => {
    toggleDocumentAttribute('data-bs-theme', settings.theme);
    toggleDocumentAttribute('data-topbar-color', settings.topbarTheme);
    toggleDocumentAttribute('data-menu-color', settings.menu.theme);
    toggleDocumentAttribute('data-sidenav-size', settings.menu.size);
    toggleDocumentAttribute('data-layout-mode', settings.layoutMode);
    return () => {
      toggleDocumentAttribute('data-bs-theme', settings.theme, true);
      toggleDocumentAttribute('data-topbar-color', settings.topbarTheme, true);
      toggleDocumentAttribute('data-sidenav-size', settings.menu.theme, true);
      toggleDocumentAttribute('data-layout-mode', settings.layoutMode, true);
    };
  }, [settings]);
  const resetSettings = useCallback(() => updateSettings(INIT_STATE), [updateSettings, INIT_STATE]);
  const contextValue = useMemo(() => ({
    ...settings,
    horizontalMenu,
    themeMode: settings.theme,
    changeTheme,
    changeLayoutMode,
    changeTopbarTheme,
    changeLayoutOrientation,
    changeMenu: {
      theme: changeMenuTheme,
      size: changeMenuSize
    },
    themeCustomizer,
    toggleBackdrop,
    resetSettings
  }), [settings, horizontalMenu, changeTheme, changeLayoutMode, changeTopbarTheme, changeLayoutOrientation, changeMenuTheme, changeMenuSize, themeCustomizer, toggleBackdrop, resetSettings]);
  return <ThemeContext.Provider value={contextValue}>
    {children}
    {offcanvasStates.showBackdrop && <div className="offcanvas-backdrop fade show" onClick={toggleBackdrop} />}
  </ThemeContext.Provider>;
};
export { LayoutProvider, useLayoutContext };